import { useCallback, useEffect, useRef, useState } from 'react';
import { useModalFocus } from '../../lib/use-modal-focus.js';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// The house, above the calendar. Deliberately not an OTA photo grid: the lead
// frame runs large with a Source Serif caption under it, the way a plate runs
// in a printed almanac, and the supporting frames sit in a hairline-separated
// bench beside it. Captions are part of the design, not alt-text overflow —
// a mute grid reads as a listing, a captioned one reads as our publication.
//
// Photos are hand-installed at /property/<slug>/<name>.jpg with a -640 variant
// beside each; `srcSet` lets phones pull the small one. Order and captions come
// from property-config so a re-shoot is a data change, not a code change.

function Frame({ photo, className = '', sizes, onOpen, index, priority = false }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(index)}
      aria-label={`View photo ${index + 1}: ${photo.caption}`}
      className={[
        'group relative block w-full overflow-hidden bg-cream-100',
        className
      ].join(' ')}
    >
      {/* width/height are the file's real pixels, from property-config. The
          frame is object-cover inside a fixed aspect ratio, so they do not
          size anything; they let the browser reserve the box and pick from
          the srcset before the first byte arrives, which is what the
          largest-paint measurement was waiting on. The srcset's large
          descriptor is the file's own width, not a hardcoded 1200: three of
          these photos are portrait and 722 or 701 wide, and claiming 1200
          sent phones the wrong candidate. */}
      <img
        src={photo.src}
        srcSet={`${photo.src640} 640w, ${photo.src} ${photo.width}w`}
        sizes={sizes}
        width={photo.width}
        height={photo.height}
        alt={photo.alt || photo.caption}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding={priority ? 'sync' : 'async'}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
      />
      {/* Hairline inset instead of a border: keeps the frame edge crisp against
          the cream page without adding a visible box. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-navy-900/10"
      />
    </button>
  );
}

function Lightbox({ photos, index, onClose, onStep }) {
  const closeRef = useRef(null);
  const panelRef = useRef(null);
  const photo = photos[index];

  // Escape, the Tab trap, the scroll lock and the focus restore are the shared
  // overlay behaviour, now in one place so the booking overlay cannot drift
  // from it again. The arrow keys are this dialog's own.
  useModalFocus(true, panelRef, { onEscape: onClose, initialFocusRef: closeRef });

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowLeft') onStep(-1);
      else if (e.key === 'ArrowRight') onStep(1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onStep]);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} of ${photos.length}: ${photo.caption}`}
      className="fixed inset-0 z-50 flex flex-col bg-navy-950/95 px-4 pb-4 pt-3 backdrop-blur-sm sm:px-6"
    >
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between">
        {/* Inter, not the display serif, so the counter matches every other kicker. */}
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-cream-400">
          {index + 1} <span className="text-cream-600">/</span> {photos.length}
        </p>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close photos"
          className="-mr-2 rounded-full p-2 text-cream-200 hover:bg-navy-800 hover:text-cream-50"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="relative mx-auto flex w-full max-w-5xl flex-1 items-center justify-center py-3">
        <img
          key={photo.src}
          src={photo.src}
          width={photo.width}
          height={photo.height}
          alt={photo.alt || photo.caption}
          className="max-h-full max-w-full object-contain"
        />
        {photos.length > 1 && (
          <>
            <StepButton dir={-1} onStep={onStep} />
            <StepButton dir={1} onStep={onStep} />
          </>
        )}
      </div>

      <div className="mx-auto w-full max-w-5xl text-center">
        <div className="mx-auto mb-2 h-px w-16 bg-cream-600/50" />
        <p className="font-serif text-sm italic text-cream-200">{photo.caption}</p>
      </div>
    </div>
  );
}

function StepButton({ dir, onStep }) {
  const Icon = dir < 0 ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={() => onStep(dir)}
      aria-label={dir < 0 ? 'Previous photo' : 'Next photo'}
      className={[
        'absolute top-1/2 -translate-y-1/2 rounded-full bg-navy-900/70 p-2.5 text-cream-100',
        'hover:bg-navy-800 hover:text-cream-50',
        dir < 0 ? 'left-0 sm:-left-2' : 'right-0 sm:-right-2'
      ].join(' ')}
    >
      <Icon className="h-6 w-6" aria-hidden="true" />
    </button>
  );
}

export default function Gallery({ photos = [], propertyName }) {
  const [open, setOpen] = useState(null);

  const openAt = useCallback((i) => setOpen(i), []);
  // Focus goes back to the frame that opened the lightbox; useModalFocus
  // captures the trigger on open and restores it on unmount.
  const close = useCallback(() => setOpen(null), []);

  const step = useCallback(
    (d) => setOpen((i) => (i == null ? i : (i + d + photos.length) % photos.length)),
    [photos.length]
  );

  if (!photos.length) return null;

  const [lead, ...rest] = photos;

  return (
    <section aria-label={`Photos of ${propertyName}`} className="mt-6">
      {/* Full-bleed on phones (the 16px page gutter wastes width that the lead
          frame should be using); back inside the measure at sm+. */}
      <div className="-mx-4 sm:mx-0">
        {/* Two columns on phones so the supporting pair sits side by side under
            the lead (a single column would stack three full-width 4:3 frames and
            push the calendar off the second screen); three at sm+, where the lead
            spans 2x2 and the pair stacks beside it. */}
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          <Frame
            photo={lead}
            index={0}
            onOpen={openAt}
            priority
            sizes="(min-width: 640px) 66vw, 100vw"
            className="col-span-2 aspect-[4/3] sm:row-span-2 sm:aspect-auto sm:rounded-l-xl"
          />
          {/* Two supporting frames stack beside the lead at sm+; on phones they
              join a two-up row under it. */}
          {rest.slice(0, 2).map((p, i) => (
            <Frame
              key={p.src}
              photo={p}
              index={i + 1}
              onOpen={openAt}
              sizes="(min-width: 640px) 33vw, 50vw"
              className={[
                'aspect-[4/3]',
                i === 0 ? 'sm:rounded-tr-xl' : 'sm:rounded-br-xl'
              ].join(' ')}
            />
          ))}
        </div>
      </div>

      <div className="mt-2.5 flex items-baseline justify-between gap-4">
        <p className="font-serif text-sm italic text-navy-500">{lead.caption}</p>
        {/* -my-3 py-3: the 16px link becomes a 40px tap target and the
            negative margins hand the height back, so the caption row stays. */}
        <button
          type="button"
          onClick={() => openAt(0)}
          className="-my-3 shrink-0 py-3 text-xs font-medium uppercase tracking-[0.16em] text-cream-600 no-underline hover:text-navy-900"
        >
          All {photos.length} photos <span aria-hidden="true">›</span>
        </button>
      </div>

      {open != null && (
        <Lightbox photos={photos} index={open} onClose={close} onStep={step} />
      )}
    </section>
  );
}
